import { useState } from 'react';
import { DataTable } from '@/components/tables';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MoreHorizontal, Eye, FileText, XCircle } from 'lucide-react';
import { formatCurrency, formatDate, cn, getStatusColor } from '@/lib/utils';
import { mockOrders } from '@/lib/mock-data';

const columns = [
  {
    accessorKey: 'id',
    header: 'Order ID',
    cell: ({ row }) => <span className="font-mono font-medium">{row.original.id}</span>,
  },
  {
    accessorKey: 'customer',
    header: 'Customer',
  },
  {
    accessorKey: 'items',
    header: 'Items',
    cell: ({ row }) => <span>{row.original.items} items</span>,
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    cell: ({ row }) => <span className="font-semibold">{formatCurrency(row.original.amount)}</span>,
  },
  {
    accessorKey: 'paymentMethod',
    header: 'Payment',
    cell: ({ row }) => (
      <Badge variant="secondary">{row.original.paymentMethod}</Badge>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge className={cn('capitalize', getStatusColor(row.original.status))}>
        {row.original.status}
      </Badge>
    ),
  },
  {
    accessorKey: 'date',
    header: 'Date',
    cell: ({ row }) => formatDate(row.original.date),
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem>
            <Eye className="h-4 w-4 mr-2" />
            View Details
          </DropdownMenuItem>
          <DropdownMenuItem>
            <FileText className="h-4 w-4 mr-2" />
            Generate Invoice
          </DropdownMenuItem>
          <DropdownMenuItem className="text-destructive">
            <XCircle className="h-4 w-4 mr-2" />
            Cancel Order
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  },
];

export default function OrdersPage() {
  const [orders] = useState(mockOrders);
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredOrders = statusFilter === 'all'
    ? orders
    : orders.filter((o) => o.status === statusFilter);

  const orderStats = {
    total: orders.length,
    completed: orders.filter((o) => o.status === 'completed').length,
    processing: orders.filter((o) => o.status === 'processing').length,
    pending: orders.filter((o) => o.status === 'pending').length,
    cancelled: orders.filter((o) => o.status === 'cancelled').length,
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Orders</h1>
          <p className="text-muted-foreground">Manage and track all orders</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="p-4 rounded-xl border bg-card">
          <p className="text-sm text-muted-foreground">Total</p>
          <p className="text-2xl font-bold">{orderStats.total}</p>
        </div>
        <div className="p-4 rounded-xl border bg-green-50 dark:bg-green-900/20">
          <p className="text-sm text-green-600">Completed</p>
          <p className="text-2xl font-bold text-green-700">{orderStats.completed}</p>
        </div>
        <div className="p-4 rounded-xl border bg-blue-50 dark:bg-blue-900/20">
          <p className="text-sm text-blue-600">Processing</p>
          <p className="text-2xl font-bold text-blue-700">{orderStats.processing}</p>
        </div>
        <div className="p-4 rounded-xl border bg-yellow-50 dark:bg-yellow-900/20">
          <p className="text-sm text-yellow-600">Pending</p>
          <p className="text-2xl font-bold text-yellow-700">{orderStats.pending}</p>
        </div>
        <div className="p-4 rounded-xl border bg-red-50 dark:bg-red-900/20">
          <p className="text-sm text-red-600">Cancelled</p>
          <p className="text-2xl font-bold text-red-700">{orderStats.cancelled}</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <div className="overflow-x-auto">
          <TabsList className="w-max min-w-full sm:min-w-0">
            <TabsTrigger value="all">All Orders</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
            <TabsTrigger value="processing">Processing</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
          </TabsList>
        </div>
      </Tabs>

      <DataTable
        columns={columns}
        data={filteredOrders}
        searchPlaceholder="Search orders..."
      />
    </div>
  );
}
